import {diApiEu} from '@di-shared/api/di';
import type {ApiResponseMonthlyDrivingInfoResponse, ApiResponseTripDaysResponse, TripDay} from '@di-shared/api/di/__generated_eu';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';
import {DRIVING_DISTANCE_GRAPH_EU} from '@di-shared/features/history/driving-info/DrivingInfoDistance/.eu';
import {
  endDateOfMonth,
  isDateSameWeek,
  isSameYearAndMonth,
  monthOfInterval,
  parseDateWithFormat,
  setDay,
  startDateOfMonth,
  weekOfInterval,
} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQueries} from '@tanstack/react-query';

function getFocusTripDate(selectedDate: Date, tripDays: Array<TripDay>) {
  const today = new Date();
  const isCurrentMonth = isSameYearAndMonth(today, selectedDate);
  const existTripDays = tripDays.filter((trip) => trip.tripCnt > 0);

  // 주행기록이 없는 경우 (당월: 당일 | 전월: 최초일)
  if (existTripDays.length === 0) {
    return isCurrentMonth ? today : setDay(selectedDate, tripDays[0].day);
  }

  // 주행기록이 있는 경우 (당월: 최근 주행 | 전월: 최초 주행)
  return setDay(selectedDate, isCurrentMonth ? existTripDays[existTripDays.length - 1].day : existTripDays[0].day);
}

export function isTripOnDay(tripDays: Array<TripDay>, day: number) {
  return tripDays.some((trip) => trip.day === day && trip.tripCnt > 0);
}

export function getWeeksOfMonth(date: Date) {
  const monthStart = startDateOfMonth(date);
  const monthEnd = endDateOfMonth(date);

  return weekOfInterval(monthStart, monthEnd);
}

export function getWeekIndexOfDate(date: Date) {
  return getWeeksOfMonth(date).findIndex((weekStart) => isDateSameWeek(date, weekStart));
}

export default function useTripDayEu({vin, selectedDate}: {vin: string; selectedDate: Date}) {
  const [{data: drivingData}, {data: tripDayData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveDrivingInfo), vin],
        queryFn: () => diApiEu.drivingHistory.retrieveDrivingInfo({vin}),
        select: ({data}: ApiResponseMonthlyDrivingInfoResponse) => {
          // 빈 배열이면 당월 데이터만 채워서 반환
          if (!data.drivingDistances || data.drivingDistances.length === 0) {
            return {
              ...data,
              drivingDistances: [
                {
                  date: getYYYYMMFormat(new Date()),
                  totalDistance: 0,
                },
              ],
            };
          }

          const earliestDate = parseDateWithFormat(data.drivingDistances[0].date);
          if (!earliestDate) {
            throw new Error('earliestDate parse error');
          }

          // AS-IS 주행기록 월별 데이터가 내려오면서, 주행 없는 경우는 BE에서 빈값을 배열에 채워서 내려줌
          // TO-BE BE 원천이 변경되면서 스펙이 변경 됨, FE에서 빈값을 배열에 직접 채우는 것으로 수정
          const filledDrivingDistances = monthOfInterval(earliestDate, new Date()).map((month) => {
            const existingData = data.drivingDistances.find((item) => item.date === getYYYYMMFormat(month));
            if (existingData) {
              return existingData;
            } else {
              return {
                date: getYYYYMMFormat(month),
                totalDistance: 0,
              };
            }
          });

          const maxDrivingDistance = filledDrivingDistances.slice(-DRIVING_DISTANCE_GRAPH_EU.LENGTH);

          return {
            ...data,
            drivingDistances: maxDrivingDistance,
          };
        },
      },
      {
        queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveTripDays1), vin, getYYYYMMFormat(selectedDate)],
        queryFn: () =>
          diApiEu.drivingHistory.retrieveTripDays1({
            vin,
            month: getYYYYMMFormat(selectedDate),
          }),
        select: ({data}: ApiResponseTripDaysResponse) => data,
      },
    ],
  });

  const verifiedDrivingDistances = drivingData.drivingDistances.map((driving) => {
    const verifiedDate = parseDateWithFormat(driving.date);
    if (!verifiedDate) {
      throw new Error('drivingDistances parse error');
    }
    return {
      ...driving,
      verifiedDate,
    };
  });

  const focusTripDate = getFocusTripDate(selectedDate, tripDayData.trips);

  return {
    tripDays: tripDayData.trips,
    minDate: verifiedDrivingDistances[0].verifiedDate,
    focusTripDate,
  };
}
